import React, {useEffect, useState} from 'react';
import {ConfigProvider, Grid, Layout, message, Spin, Typography} from 'antd';
import koKR from 'antd/locale/ko_KR';
import {BrowserRouter, Link, Navigate, Route, Routes, useNavigate} from 'react-router-dom';
import MainMenu from 'components/MainMenu';
import UserProfileModal from 'components/UserProfileModal';
import UserProfileEditModal from 'components/UserProfileEditModal';
import LoginModal from 'components/LoginModal';
import {TableProvider} from './contexts/TableContext';
import 'antd/dist/reset.css';
import 'App.css';
import {AnimatePresence} from 'framer-motion';
import UserMenu from 'components/UserMenu';
import useLogout from 'hooks/useLogout';
import MetaDataImg from './MetaData.png';
import ProjectList from 'components/ProjectList';
import TermDataList from 'components/TermDataList';
import WordDataList from 'components/WordDataList';
import DomainDataList from 'components/DomainDataList';
import SystemSettingsPage from 'components/SystemSettingsPage';
import {MenuProvider, useMenuContext} from './contexts/MenuContext';
import PrivateRoute from './components/routing/PrivateRoute';
import NotFoundPage from './components/routing/NotFoundPage';
import ForbiddenPage from './components/routing/ForbiddenPage';
import {resolveComponent} from './components/routing/componentRegistry';
import SignupPage from 'components/SignupPage';
import {guestLogin, isGuestUser} from './utils/guestAuth';
import {ensureFreshToken, getStoredToken} from 'api/http';
import HomePage from 'components/HomePage';
import GroupManagement from 'components/GroupManagement';
import UserManagement from 'components/UserManagement';
import AuthorityManagement from 'components/AuthorityManagement';
import APIManagement from 'components/APIManagement';
import EventManagement from 'components/EventManagement';
import ProjectWordManagement from 'components/ProjectWordManagement'; // 프로젝트 단어 관리 추가
import HelpPage from 'components/HelpPage';
import BoardPage from 'components/BoardPage';
import UserSettingsModal from 'components/UserSettingsModal';

const { Header, Content } = Layout;
const { Title } = Typography;

// 동적 라우트 빌더 (MenuContext 이용)
const DynamicRoutes = ({ isAuthenticated }) => {
  const { menus, loading, loadedOnce } = useMenuContext();

  // 로딩 시 스피너 (최초 로드)
  if (loading && !loadedOnce) {
    return <div style={{padding:40,textAlign:'center'}}><Spin size="large"/></div>;
  }

  const dynamicRouteElems = (menus || [])
    .filter(m => m.path && m.rteKey)
    .map(m => {
      const Comp = resolveComponent(m.rteKey);
      const element = !Comp ? <NotFoundPage /> : <Comp />; // authReqYn 분기 제거
      return <Route key={m.path} path={m.path} element={element} />;
    });

  // 기본(레거시 고정) 라우트들 - 중복되지 않으면 추가
  const ensure = (path, elem, needAuth=false) => {
    if (dynamicRouteElems.find(r => r.props.path === path)) return null;
    return <Route key={path} path={path} element={needAuth ? <PrivateRoute isAuthenticated={isAuthenticated} element={elem} /> : elem} />;
  };

  return (
    <Routes>
      {dynamicRouteElems}
      {ensure('/home', <HomePage />)}
      {ensure('/projects', <ProjectList />, true)}
      {ensure('/projects/:pjtId/words', <ProjectWordManagement />, true)}
      {ensure('/terms', <TermDataList />)}
      {ensure('/words', <WordDataList />)}
      {ensure('/domains', <DomainDataList />)}
  {ensure('/board', <BoardPage />)}
  {ensure('/boards', <BoardPage />)}
      {ensure('/system/menus', <SystemSettingsPage />, true)}
      {ensure('/system/groups', <GroupManagement />, true)}
      {ensure('/system/users', <UserManagement />, true)}
      {ensure('/system/authorities', <AuthorityManagement />, true)}
      {ensure('/system/apis', <APIManagement />, true)}
      {ensure('/system/events', <EventManagement />, true)}
      {ensure('/signup', <SignupPage />)}
      {ensure('/help', <HelpPage />)}
      <Route path="/403" element={<ForbiddenPage />} />
      <Route path="/404" element={<NotFoundPage />} />
      <Route path="/" element={<Navigate to="/home" replace />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

function AppInner() {
  const screens = Grid.useBreakpoint();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const navigate = useNavigate();

  // 초기 구동: 저장된 토큰 확인 후 없으면 guest 로그인
  useEffect(() => {
    const init = async () => {
      try {
        let session = null;
        try {
          session = await ensureFreshToken();
        } catch (_) {
          session = null;
        }

        if (!session) {
          try {
            session = getStoredToken();
          } catch (_) {
            session = null;
          }
        }

        if (session) {
          setUser(session);
          setIsAuthenticated(!isGuestUser(session));
        } else {
          const g = await guestLogin();
          if (g) {
            setUser(g); // isAuthenticated 그대로 false
          }
          setIsAuthenticated(false);
        }
        // 메뉴 새로고침 및 권한 캐시 무효화 알림
        window.dispatchEvent(new CustomEvent('refresh-menus'));
        window.dispatchEvent(new CustomEvent('auth-context-changed'));
      } finally {
        setInitializing(false);
      }
    };
    init();
  }, []);

  // signupPage 등에서 전역 이벤트로 로그인 모달 열기 요청 처리
  useEffect(() => {
    const openHandler = () => setIsLoginModalOpen(true);
    window.addEventListener('open-login-modal', openHandler);
    return () => window.removeEventListener('open-login-modal', openHandler);
  }, []);

  // 401 처리: guest 재로그인 시도 (실패 시 로그인 모달)
  useEffect(() => {
    const handler = async (e) => {
      localStorage.removeItem('userToken');
      setUser(null);
      setIsAuthenticated(false);
      const g = await guestLogin();
      if (g) {
        setUser(g);
        window.dispatchEvent(new CustomEvent('refresh-menus'));
        window.dispatchEvent(new CustomEvent('auth-context-changed'));
      } else {
        const msg = e?.detail?.message || '인증이 필요합니다.';
        message.error(msg);
        setIsLoginModalOpen(true);
      }
    };
    window.addEventListener('auth-required', handler);
    return () => window.removeEventListener('auth-required', handler);
  }, []);

  // 다른 탭에서의 로그인/로그아웃 동기화
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== 'userToken') return;
      try {
        const val = e.newValue;
        const parsed = val ? JSON.parse(val) : null;
        setUser(parsed);
        setIsAuthenticated(!!(parsed && !isGuestUser(parsed)));
        window.dispatchEvent(new CustomEvent('refresh-menus'));
        window.dispatchEvent(new CustomEvent('auth-context-changed'));
      } catch (_) {
        // ignore parse error
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // 토큰이 갱신되었을 때 현재 탭 상태 동기화
  useEffect(() => {
    const onTokenRefreshed = (event) => {
      const detail = event?.detail;
      if (!detail) return;
      setUser(detail);
      setIsAuthenticated(!!detail && !isGuestUser(detail));
    };
    window.addEventListener('token-refreshed', onTokenRefreshed);
    return () => window.removeEventListener('token-refreshed', onTokenRefreshed);
  }, []);

  // 사용자 변경 시 항상 HOME으로 이동
  useEffect(() => {
    const onAuthContextChanged = () => {
      navigate('/home', { replace: true });
    };
    window.addEventListener('auth-context-changed', onAuthContextChanged);
    return () => window.removeEventListener('auth-context-changed', onAuthContextChanged);
  }, [navigate]);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
    setIsAuthenticated(true);
    setIsLoginModalOpen(false);
    if (userData) {
      window.dispatchEvent(new CustomEvent('token-refreshed', { detail: userData }));
    }
    window.dispatchEvent(new CustomEvent('refresh-menus'));
    window.dispatchEvent(new CustomEvent('auth-context-changed'));
  };

  // 프로필 수정 시작: 보기 모달에서 수정 클릭 시 편집 모달 오픈
  const handleOpenProfileEdit = () => {
    setIsProfileModalOpen(false);
    setTimeout(() => setIsProfileEditOpen(true), 0);
  };

  // 프로필 수정 완료 시: 상태 및 로컬스토리지 사용자 정보 병합 갱신
  const handleProfileEditSuccess = (updated) => {
    try {
      const editable = ['userNm','profileUrl','eml'];
      const merged = { ...(user || {}), ...Object.fromEntries(editable.map(k => [k, updated?.[k] ?? user?.[k]])) };
      setUser(merged);
      try {
        const raw = localStorage.getItem('userToken');
        if (raw) {
          const tokenObj = JSON.parse(raw);
          const saved = { ...tokenObj, ...Object.fromEntries(editable.map(k => [k, merged[k]])) };
          localStorage.setItem('userToken', JSON.stringify(saved));
        }
      } catch (_) { /* ignore */ }
      window.dispatchEvent(new CustomEvent('refresh-menus'));
      message.success('프로필 정보가 업데이트되었습니다.');
    } finally {
      setIsProfileEditOpen(false);
    }
  };

  const logout = useLogout({
    user,
    onAfterLogout: async () => {
      // 실제 사용자 로그아웃 후 guest 로그인
      localStorage.removeItem('userToken');
      setUser(null);
      setIsAuthenticated(false);
      const g = await guestLogin();
      if (g) {
        setUser(g); // guest 는 인증 false
        window.dispatchEvent(new CustomEvent('refresh-menus'));
        window.dispatchEvent(new CustomEvent('auth-context-changed'));
      }
    }
  });

  if (initializing) {
    return (
      <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',height:'100vh'}}>
        <Spin size="large" />
        <div style={{marginTop:12,color:'#666'}}>초기화 중...</div>
      </div>
    );
  }

  return (
    <ConfigProvider locale={koKR}>
      <TableProvider>
        <MenuProvider>
          <Layout style={{ minHeight: '100vh' }}>
            <Header style={{
              background: '#fff',
              padding: screens.xs ? '0 12px' : '0 24px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'relative',
            height: screens.xs ? '56px' : '64px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 1, overflow: 'hidden', flex: 1, minWidth: 0 }}>
              <Link to="/home" aria-label="홈으로 이동" style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                <img 
                  src={MetaDataImg} 
                  alt="MetaData" 
                  style={{ 
                    height: screens.xs ? 48 : 65, 
                    marginRight: screens.xs ? 8 : 12, 
                    cursor: 'pointer' 
                  }} 
                />
              </Link>
              {!screens.xs && (
                <Title level={3} style={{ margin: 0, color: '#1890ff', whiteSpace: 'nowrap' }}>메타데이터 관리 시스템</Title>
              )}
              {screens.xs && (
                <Title level={5} style={{ margin: 0, color: '#1890ff', fontSize: '14px' }}>메타데이터 관리</Title>
              )}
            </div>
            <div style={{ position: 'relative', zIndex: 1, flexShrink: 0 }}>
              <UserMenu
                isAuthenticated={isAuthenticated}
                user={user}
                isGuest={isGuestUser(user)}
                onLoginClick={() => setIsLoginModalOpen(true)}
                onProfileClick={() => setIsProfileModalOpen(true)}
                onLogout={logout}
                onSettingsClick={() => setIsSettingsModalOpen(true)}
                onHelpClick={() => navigate('/help')}
              />
            </div>
          </Header>
          <Layout>
            <MainMenu isAuthenticated={isAuthenticated} />
            <Content style={{ 
              margin: screens.xs ? '12px 8px' : '24px', 
              minHeight: 'auto', 
              display: 'flex', 
              flexDirection: 'column' 
            }}>
              <AnimatePresence mode="wait">
                <DynamicRoutes isAuthenticated={isAuthenticated} />
              </AnimatePresence>
            </Content>
          </Layout>
          <LoginModal open={isLoginModalOpen} onCancel={() => setIsLoginModalOpen(false)} onSuccess={handleLoginSuccess} onSignupClick={() => { setIsLoginModalOpen(false); navigate('/signup'); }} />
          {isAuthenticated && (
            <>
              <UserProfileModal open={isProfileModalOpen} onCancel={() => setIsProfileModalOpen(false)} onEdit={handleOpenProfileEdit} user={user} />
              <UserProfileEditModal open={isProfileEditOpen} user={user} onCancel={() => setIsProfileEditOpen(false)} onSuccess={handleProfileEditSuccess} />
              <UserSettingsModal
                open={isSettingsModalOpen}
                onCancel={() => setIsSettingsModalOpen(false)}
                onRefreshed={() => setIsSettingsModalOpen(false)}
                user={user}
              />
            </>
          )}
        </Layout>
      </MenuProvider>
    </TableProvider>
    </ConfigProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppInner />
    </BrowserRouter>
  );
}

export default App;
